import { Modal, ModalBody, Spinner } from "flowbite-react";
import { Button } from "../Button";
import { TriangleAlert, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../utils";
import { Typography } from "../Typography";
import { ReactNode } from "react";

type TCommonModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  size: string;
  buttonCancel?: () => void;
  buttonAccept?: () => void;
  msg?: string | ReactNode;
  acceptText?: string;
  loading?: boolean;
  id?: string;
  dataTestId?: string;
};

export function CommonDeleteModal({
  open,
  onClose,
  size,
  buttonCancel,
  buttonAccept,
  msg,
  title,
  acceptText,
  loading = false,
  id,
  dataTestId,
}: TCommonModalProps) {
  const { t, i18n } = useTranslation(["common"]);
  const isRTL = i18n.language === "ar";
  return (
    <Modal
      show={open}
      onClose={onClose}
      size={size}
      className="custom-modal-overlay"
      data-testid={dataTestId}
    >
      <div>
        <section className="border-b-0 py-1 ">
          {/* Close Button */}
          <Button
            variant={"ghost"}
            size={"icon"}
            className={cn("absolute top-4", isRTL ? "left-4" : "right-4")}
            onClick={onClose}
            id={`${id}-close-button`}
            data-testid="delete-modal-close-button"
          >
            <X size={24} />
          </Button>
        </section>
        <ModalBody className="text-center py-3 px-8">
          <figure className="bg-destructive/10 rounded-full w-11 h-11 m-auto flex items-center justify-center dark:bg-destructive/20 brightness-90">
            <TriangleAlert className="text-destructive" size={25} />
          </figure>
          <Typography
            variant={"h4"}
            affects={"medium"}
            className="text-center mb-1 mt-1 text-destructive break-words"
          >
            {title ? title : t("DELETE_MODAL.TITLE")}
          </Typography>
          <Typography variant={"p"} className="text-gray tracking-wider">
            {msg ?? t("common:DELETE_MODAL.MESSAGE")}
          </Typography>
          <section className="my-6">
            <Button
              type="button"
              variant="outline"
              size={"sm"}
              className="mx-2 button-min-width"
              onClick={buttonCancel}
              disabled={loading}
              id={`${id}-cancel-button`}
              data-testid="delete-cancel-button"
            >
              {t("common:CANCEL")}
            </Button>
            <Button
              variant="destructive"
              type={"button"}
              size={"sm"}
              className="button-min-width"
              onClick={buttonAccept}
              disabled={loading}
              id={`${id}-confirm-button`}
              data-testid="delete-confirm-button"
            >
              {loading ? (
                <>
                  {t("common:DELETING")}
                  <Spinner
                    size="sm"
                    className="ms-1"
                    data-testid="delete-modal-spinner"
                  />
                </>
              ) : (
                (acceptText ?? t("common:DELETE"))
              )}
            </Button>
          </section>
        </ModalBody>
      </div>
    </Modal>
  );
}
